import { CircleCheck, Laptop, ShieldX } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { ApiError } from "@/api/client";
import { useDecideDeviceAuth, useDeviceAuth, type DeviceAuthRequest } from "@/api/queries";
import { useMe } from "@/app/auth";
import { Field } from "@/components/common/Field";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { errorText } from "@/lib/errors";
import { formatShortDateTime } from "@/lib/format";

import { AuthShell } from "./AuthShell";

/** Formats what the user types as XXXX-XXXX. */
function normalizeCode(raw: string): string {
  const letters = raw
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .slice(0, 8);
  return letters.length > 4 ? `${letters.slice(0, 4)}-${letters.slice(4)}` : letters;
}

/** Approval of a tracker sign-in: the tracker shows a code and opens this page. */
export function DevicePage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const code = normalizeCode(params.get("code") ?? "");
  const [typed, setTyped] = useState(code);
  const [decision, setDecision] = useState<"approved" | "denied" | null>(null);
  const request = useDeviceAuth(code.length === 9 ? code : "");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setDecision(null);
    setParams({ code: typed }, { replace: true });
  };
  const reset = () => {
    setDecision(null);
    setTyped("");
    setParams({}, { replace: true });
  };

  if (decision) return <Decided decision={decision} />;

  const notFound = request.error instanceof ApiError && request.error.status === 404;
  return (
    <AuthShell title={t("device.title")} subtitle={request.data ? undefined : t("device.subtitle")}>
      {request.data ? (
        <RequestCard request={request.data} onDecided={setDecision} onOther={reset} />
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label={t("device.code")}>
            <Input
              autoFocus
              required
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="ABCD-EFGH"
              className="h-12 text-center font-mono text-xl tracking-[0.2em]"
              value={typed}
              onChange={(e) => setTyped(normalizeCode(e.target.value))}
            />
          </Field>
          {request.isFetching && <Skeleton className="h-10" />}
          {request.isError && (
            <Alert variant="danger">{notFound ? t("device.notFound") : errorText(t, request.error)}</Alert>
          )}
          <Button type="submit" size="lg" disabled={typed.length !== 9 || request.isFetching}>
            {t("device.continue")}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

function RequestCard({
  request,
  onDecided,
  onOther,
}: {
  request: DeviceAuthRequest;
  onDecided: (d: "approved" | "denied") => void;
  onOther: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const me = useMe();
  const decide = useDecideDeviceAuth();
  const d = request.device;
  const rows: [string, string][] = [
    [t("device.account"), `${me.user.full_name}\n${me.user.email}`],
    [t("device.computer"), d.name],
    [t("device.system"), `${t(`os.${d.os}`)} ${d.os_version}`.trim()],
    [t("device.version"), d.agent_version || "—"],
    [t("device.requested"), formatShortDateTime(request.created_at, lang)],
    ...(request.ip ? ([[t("device.ip"), request.ip]] as [string, string][]) : []),
  ];

  const act = async (approve: boolean) => {
    try {
      await decide.mutateAsync({ code: request.user_code, approve });
      onDecided(approve ? "approved" : "denied");
    } catch {
      // Shown below.
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
          <Laptop className="size-5" />
        </span>
        <div>
          <div className="font-semibold">{t("device.request")}</div>
          <div className="font-mono text-muted">{request.user_code}</div>
        </div>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted">{k}</dt>
            <dd className="min-w-0 font-medium break-words whitespace-pre-line">{v}</dd>
          </div>
        ))}
      </dl>
      {request.status === "pending" ? (
        <>
          <Alert variant="warning">
            {t("device.warning", {
              time: new Date(request.expires_at).toLocaleTimeString(lang, {
                hour: "2-digit",
                minute: "2-digit",
              }),
            })}
          </Alert>
          {decide.isError && <Alert variant="danger">{errorText(t, decide.error)}</Alert>}
          <div className="flex flex-col gap-2">
            <Button size="lg" disabled={decide.isPending} onClick={() => void act(true)}>
              {t("device.approve")}
            </Button>
            <Button variant="secondary" disabled={decide.isPending} onClick={() => void act(false)}>
              {t("device.deny")}
            </Button>
          </div>
        </>
      ) : (
        <Alert variant={request.status === "denied" || request.status === "expired" ? "danger" : "success"}>
          {t(`device.closed.${request.status}`)}
        </Alert>
      )}
      <Button variant="ghost" size="sm" onClick={onOther}>
        {t("device.otherCode")}
      </Button>
    </div>
  );
}

function Decided({ decision }: { decision: "approved" | "denied" }) {
  const { t } = useTranslation();
  const approved = decision === "approved";
  const Icon = approved ? CircleCheck : ShieldX;
  return (
    <AuthShell title={approved ? t("device.approvedTitle") : t("device.deniedTitle")}>
      <div className="flex flex-col items-center gap-4 text-center">
        <Icon className={approved ? "size-12 text-success" : "size-12 text-danger"} aria-hidden />
        <p>{approved ? t("device.approvedText") : t("device.deniedText")}</p>
        <Button asChild variant="secondary">
          <Link to="/">{t("device.toPortal")}</Link>
        </Button>
      </div>
    </AuthShell>
  );
}
